import React, { useMemo } from "react";
import { Container, Graphics, Text } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { formatToCustomLocale } from "../../../../utilities";
import {
  FONT_CONFIG,
  TEXT_STYLE_CONFIG,
} from "../../../../textureConfigs";
import { selectBet, selectBets, selectCurrency } from "../../../../State/selectors";
import BonusButtonDecrementBet from "../Buttons/BonusButtonDecrementBet";
import BonusButtonIncrementBet from "../Buttons/BonusButtonIncrementBet";

// Reference dimensions at 800px popup width
// All values scale uniformly with popupScale
const REFERENCE_CONFIG = {
  // Background dimensions (at reference popup width of 800px)
  backgroundHeight: 110, // Height of bet value background (increased from 80)
  backgroundPaddingX: 80, // Horizontal padding inside background (increased from 60)
  borderRadius: 12, // Border radius for rounded corners

  // Button size matches background height for perfect alignment
  buttonSize: 110, // Same as backgroundHeight (increased from 80)

  // Gap between buttons and background (also used for label gap above background)
  gap: 30, // Increased from 20

  // Font sizes
  labelFontSize: 38, // Increased from 28
  valueFontSize: 48, // Increased from 36

  // Background color and opacity
  backgroundColor: 0x2a2a2a, // Dark gray color
  backgroundOpacity: 0.8, // 80% opacity
};

const BonusBetSelector = ({ popupScale, position }) => {
  const { t } = useTranslation();
  const bet = useSelector(selectBet);
  const bets = useSelector(selectBets);
  const currency = useSelector(selectCurrency);

  // Scale all dimensions uniformly based on popup scale
  const scaledConfig = useMemo(() => {
    return {
      backgroundHeight: Math.round(REFERENCE_CONFIG.backgroundHeight * popupScale),
      backgroundPaddingX: Math.round(REFERENCE_CONFIG.backgroundPaddingX * popupScale),
      borderRadius: Math.round(REFERENCE_CONFIG.borderRadius * popupScale),
      buttonSize: Math.round(REFERENCE_CONFIG.buttonSize * popupScale),
      gap: Math.round(REFERENCE_CONFIG.gap * popupScale),
      labelFontSize: Math.round(REFERENCE_CONFIG.labelFontSize * popupScale),
      valueFontSize: Math.round(REFERENCE_CONFIG.valueFontSize * popupScale),
    };
  }, [popupScale]);

  // Prepare text content
  const betLabelText = t("bet").toUpperCase();
  const formattedBet = formatToCustomLocale(bet);
  const betAmountText = `${formattedBet} ${currency}`;

  // Text styles
  const betLabelStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: scaledConfig.labelFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [scaledConfig.labelFontSize]
  );

  // Calculate max bet width based on largest bet in bets array
  const maxBetWidth = useMemo(() => {
    if (!bets || bets.length === 0) {
      const tempStyle = new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: scaledConfig.valueFontSize,
        fontWeight: "700",
      });
      return TextMetrics.measureText(betAmountText, tempStyle).width;
    }

    const maxBet = Math.max(...bets);
    const maxBetText = `${formatToCustomLocale(maxBet)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: scaledConfig.valueFontSize,
      fontWeight: "700",
    });
    const maxBetMetrics = TextMetrics.measureText(maxBetText, tempStyle);

    return maxBetMetrics.width;
  }, [bets, currency, scaledConfig.valueFontSize, betAmountText]);

  // Auto-scale font size if actual bet text exceeds max width
  const finalBetFontSize = useMemo(() => {
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: scaledConfig.valueFontSize,
      fontWeight: "700",
    });
    const metrics = TextMetrics.measureText(betAmountText, tempStyle);

    // If text exceeds max width, scale down proportionally
    if (metrics.width > maxBetWidth) {
      const scale = maxBetWidth / metrics.width;
      return Math.round(scaledConfig.valueFontSize * scale);
    }

    return scaledConfig.valueFontSize;
  }, [betAmountText, scaledConfig.valueFontSize, maxBetWidth]);

  const betAmountStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalBetFontSize,
        fontWeight: "700",
        fill: 0xffffff,
        align: "center",
        ...TEXT_STYLE_CONFIG.OUTLINE,
      }),
    [finalBetFontSize]
  );

  // Measure text dimensions for positioning
  const labelMetrics = TextMetrics.measureText(betLabelText, betLabelStyle);

  // Calculate background width based on text + padding
  const backgroundWidth = Math.round(
    Math.max(labelMetrics.width, maxBetWidth) + scaledConfig.backgroundPaddingX * 2
  );

  // Text positioning
  // Label is above background with same gap as buttons
  const labelY = Math.round(-scaledConfig.backgroundHeight / 2 - scaledConfig.gap - labelMetrics.height / 2);

  // Value is centered vertically inside the background
  const amountY = 0;

  // Button positioning - buttons are same height as background, aligned vertically centered
  const totalWidth = scaledConfig.buttonSize + scaledConfig.gap + backgroundWidth + scaledConfig.gap + scaledConfig.buttonSize;

  const decrementX = Math.round(-totalWidth / 2 + scaledConfig.buttonSize / 2);
  const incrementX = Math.round(totalWidth / 2 - scaledConfig.buttonSize / 2);
  const buttonsY = 0; // Centered vertically with background

  const containerX = position?.x ?? 0;
  const containerY = position?.y ?? 0;

  return (
    <Container name="bonus_bet_selector" x={containerX} y={containerY} zIndex={5}>
      {/* Background graphics - centered with rounded corners */}
      <Graphics
        draw={(g) => {
          g.clear();
          g.beginFill(REFERENCE_CONFIG.backgroundColor, REFERENCE_CONFIG.backgroundOpacity);
          g.drawRoundedRect(
            -backgroundWidth / 2,
            -scaledConfig.backgroundHeight / 2,
            backgroundWidth,
            scaledConfig.backgroundHeight,
            scaledConfig.borderRadius
          );
          g.endFill();
        }}
        resolution={2}
      />

      {/* Label text */}
      <Text
        text={betLabelText}
        style={betLabelStyle}
        anchor={{ x: 0.5, y: 0.5 }}
        x={0}
        y={labelY}
        resolution={2}
      />

      {/* Value text */}
      <Text
        text={betAmountText}
        style={betAmountStyle}
        anchor={{ x: 0.5, y: 0.5 }}
        x={0}
        y={amountY}
        resolution={2}
      />

      {/* Decrement button - left side, vertically centered */}
      <BonusButtonDecrementBet
        x={decrementX}
        y={buttonsY}
        size={scaledConfig.buttonSize}
      />

      {/* Increment button - right side, vertically centered */}
      <BonusButtonIncrementBet
        x={incrementX}
        y={buttonsY}
        size={scaledConfig.buttonSize}
      />
    </Container>
  );
};

export default BonusBetSelector;
